import { clamp01 } from "@bfly/engine/math";
import {
  PLAIN_DOTS,
  oklabToSrgb,
  parseSrgb,
  srgbToOklab,
  type BlendMode,
  type DotLook,
  type Vec3,
} from "@bfly/engine";

/** How the portrait's face and lens draw in one theme. */
export interface PortraitLook {
  /** How the dots blend with the page color behind them. */
  blend: BlendMode;
  /** How the face's dots look. */
  dots: Readonly<DotLook>;
  /** Chroma of the face and lens inks, as a multiple of the baked palette's (in OKLab). */
  chroma: number;
  /** The butterfly's far wing: how much of the palette's color it keeps (in both renderers),
      and its opacity in the WebGL wings. */
  farWing: Readonly<{ shade: number; opacity: number }>;
}

export const LIGHT_LOOK: Readonly<PortraitLook> = Object.freeze({
  blend: "normal",
  dots: PLAIN_DOTS,
  chroma: 1,
  // On a light background, a shaded far wing reads as depth.
  farWing: Object.freeze({ shade: 0.62, opacity: 0.75 }),
});

// On a dark background the gaps between halftone dots read as a black lattice, and the muted
// inks go flat. So the dots grow big and soft enough to overlap, and the inks get more
// chroma. I picked this out of six variants.
export const DARK_LOOK: Readonly<PortraitLook> = Object.freeze({
  blend: "normal",
  dots: Object.freeze({ grow: 0.55, soft: 0.65 }),
  chroma: 1.35,
  // The light theme's shading would sink the far wing into the dark background, so here it
  // keeps its full color.
  farWing: Object.freeze({ shade: 1, opacity: 1 }),
});

const inGamut = (rgb: Vec3): boolean => rgb.every((c) => c >= -1e-4 && c <= 1 + 1e-4);

function toHex(rgb: Vec3): string {
  return `#${rgb
    .map((c) =>
      Math.round(clamp01(c) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

/** The palette with each ink's OKLab chroma scaled by `chroma`, in the same order. Lightness
    and hue stay put. If an ink ends up outside sRGB, its boost is halved until it fits. */
export function gradePalette(colors: readonly string[], chroma: number): string[] {
  if (chroma === 1) return [...colors];
  return colors.map((hex) => {
    const [lightness, a, b] = srgbToOklab(parseSrgb(hex));
    let scale = chroma;
    let rgb = oklabToSrgb([lightness, a * scale, b * scale]);
    for (let step = 0; step < 12 && !inGamut(rgb); step++) {
      scale = 1 + (scale - 1) / 2;
      rgb = oklabToSrgb([lightness, a * scale, b * scale]);
    }
    return toHex(rgb);
  });
}
